import { motion, useScroll, useTransform } from "framer-motion";
import { BadgeCheck, CreditCard, MousePointerClick, ReceiptText, type LucideIcon } from "lucide-react";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import SectionTitle from "../shared/SectionTitle";

const STEPS: [string, LucideIcon][] = [
  ["pick", MousePointerClick],
  ["transfer", CreditCard],
  ["receipt", ReceiptText],
  ["active", BadgeCheck],
];
// The reading line: the rail is drawn up to it and each step lights as it crosses it.
const LINE = "65%";

// How card-to-card payment works, as a timeline that draws itself as you read.
export default function PaymentTimeline() {
  const { t } = useTranslation();
  return (
    <section id="payment" aria-labelledby="payment-title" className="scroll-mt-[var(--nav-h)] border-t border-line py-[clamp(80px,9vw,140px)]">
      <div className="mx-auto grid w-full max-w-[1200px] gap-10 px-[var(--gutter)] md:grid-cols-[minmax(0,5fr)_minmax(0,6fr)] md:items-start md:gap-[clamp(40px,8vw,128px)]">
        <SectionTitle id="payment-title" title={t("ui.landing.payment.title")} className="md:sticky md:top-[calc(var(--nav-h)+48px)]" />
        <ol className="grid gap-[var(--gap)] [--gap:clamp(36px,5vw,56px)]">
          {STEPS.map(([k, Icon], i) => (
            <Step key={k} Icon={Icon} last={i === STEPS.length - 1} title={t(`ui.landing.payment.steps.${k}.title`)} body={t(`ui.landing.payment.steps.${k}.body`)} />
          ))}
        </ol>
      </div>
    </section>
  );
}

function Step({ Icon, last, title, body }: { Icon: LucideIcon; last: boolean; title: string; body: string }) {
  const node = useRef<HTMLSpanElement>(null);
  const rail = useRef<HTMLSpanElement>(null);
  const { scrollYProgress: lit } = useScroll({ target: node, offset: [`start ${LINE}`, `end ${LINE}`] });
  // the rail segment down to the next step (the last step has none)
  const { scrollYProgress: fill } = useScroll({ target: last ? undefined : rail, offset: [`start ${LINE}`, `end ${LINE}`] });
  const scale = useTransform(lit, [0, 1], [0.92, 1]);

  return (
    <li className="relative grid grid-cols-[52px_minmax(0,1fr)] items-start gap-5">
      {!last && (
        <span ref={rail} aria-hidden className="absolute start-[25px] top-[26px] h-[calc(100%+var(--gap))] w-0.5 overflow-hidden bg-line">
          <motion.span style={{ scaleY: fill }} className="absolute inset-0 origin-top bg-accent motion-reduce:![transform:none]" />
        </span>
      )}
      <motion.span
        ref={node}
        aria-hidden
        style={{ scale }}
        className="relative z-[1] grid size-[52px] place-items-center rounded-sm bg-surface text-ink-3 ring-1 ring-inset ring-line-2 motion-reduce:![transform:none]"
      >
        <Icon className="size-6" strokeWidth={1.75} />
        <motion.span style={{ opacity: lit }} className="absolute inset-0 grid place-items-center rounded-sm bg-accent text-on-accent motion-reduce:!opacity-100">
          <Icon className="size-6" strokeWidth={1.75} />
        </motion.span>
      </motion.span>
      <div>
        <h3 className="mb-1.5 mt-2 font-display text-[1.3125rem] font-bold leading-[1.5] text-ink">{title}</h3>
        <p className="max-w-[40ch] text-[17px] leading-[1.9] text-ink-2">{body}</p>
      </div>
    </li>
  );
}
